import { useCallback, useEffect, useRef } from 'react';
import type { ChangeEvent, RefObject } from 'react';

type UseAutoResizeOptions = {
  /**
   * Whether auto-resize is enabled
   */
  autoResize?: boolean;
  /**
   * The current value of the textarea
   */
  value?: string | number;
  /**
   * Original onChange handler
   */
  onChange?: (text: string, event?: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
};

type UseAutoResizeReturn = {
  /**
   * Ref to attach to the textarea element
   */
  textareaRef: RefObject<HTMLTextAreaElement>;
  /**
   * Enhanced onChange handler that includes auto-resize
   */
  handleChange: (text: string, event?: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
};

/**
 * Custom hook that provides auto-resize functionality for textareas
 */
export const useAutoResize = ({
  autoResize = true,
  value,
  onChange,
}: UseAutoResizeOptions): UseAutoResizeReturn => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const autoResizeTextarea = useCallback(() => {
    if (!textareaRef.current) {
      return;
    }

    textareaRef.current.style.setProperty('height', 'auto');
    textareaRef.current.style.setProperty('height', `${textareaRef.current.scrollHeight}px`);
  }, [textareaRef]);

  // Auto-resize when content changes
  useEffect(() => {
    if (autoResize) {
      autoResizeTextarea();
    }
  }, [autoResizeTextarea, autoResize, value]);

  // Enhanced onChange handler that triggers auto-resize
  const handleChange = useCallback((
    text: string,
    event?: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    if (onChange) {
      onChange(text, event);
    }

    // Auto-resize after content update
    if (autoResize) {
      // Use requestAnimationFrame to ensure the DOM has updated with the new value
      requestAnimationFrame(() => {
        autoResizeTextarea();
      });
    }
  }, [onChange, autoResizeTextarea, autoResize]);

  return {
    textareaRef,
    handleChange,
  };
};
